export const READINESS_SEVERITIES = ['required', 'recommended'] as const;
export type ReadinessSeverity = (typeof READINESS_SEVERITIES)[number];

export interface ReadinessCheckDef {
  key: string;
  severity: ReadinessSeverity;
}

export interface ReadinessSeedEntry extends ReadinessCheckDef {
  label: string;
}

/**
 * The list of readiness checks, and the only source of it. The evaluators in
 * `core/use-cases/readiness.ts` say what each key verifies; nothing reads the
 * list from the database. Changing readiness starts here.
 *
 * Array order is the order the reader sees and the order a refusal is blamed
 * in, so the first thing a skipped line reports is this list's first unmet
 * required entry.
 *
 * ONCE DEPLOYED, KEYS ARE APPEND-ONLY. Never rename or remove one; only add,
 * and only in a way older data still resolves under. A rule freezes these keys
 * into `automation_workflow_requirements` rows at creation and nothing re-syncs
 * them, so a renamed or deleted key leaves rows naming a check the gate's
 * registry no longer implements. `checkRequirements` then blocks the execution
 * with `requirement_not_implemented` rather than approving it — the rule stops
 * firing until someone reconciles the rows. It no longer *fails* the execution,
 * which is what used to make this unrecoverable: a failed execution is not
 * excluded from the next drain's queue, so one stale key put every affected rule
 * into a retry loop that ran every cron tick, for every tenant, forever.
 * Blocking is still a broken rule, just a legible one. Translations
 * (`detail.readiness.checks.*`, `form.requirements.checks.*`) and historical
 * `blocked_reason` values are keyed the same way and break the same way.
 * Renaming is only possible with a migration that reconciles those rows in the
 * same deploy.
 */
export const TRACING_READINESS_SEED: ReadinessSeedEntry[] = [
  { key: 'order_number', label: 'PO number', severity: 'required' },
  { key: 'buyer_linked', label: 'Buyer linked', severity: 'required' },
  { key: 'supplier_linked', label: 'Supplier linked', severity: 'required' },
  {
    key: 'supplier_facility_process',
    label: 'Supplier facility process',
    severity: 'required',
  },
  {
    key: 'buyer_facility_process',
    label: 'Buyer facility process',
    severity: 'required',
  },
  {
    key: 'tracing_template',
    label: 'Default tracing attachment template',
    severity: 'required',
  },
  { key: 'order_line', label: 'At least one order line', severity: 'required' },
  {
    key: 'product_reference',
    label: 'Product reference on every line',
    severity: 'required',
  },
  {
    key: 'line_quantity',
    label: 'Quantity on every line',
    severity: 'required',
  },
  { key: 'line_unit', label: 'Unit on every line', severity: 'required' },
  // Both seller-profile fields belong to the counterparty, not to the order:
  // useful for reconciliation, never a reason to refuse a tracing request.
  {
    key: 'seller_official_name',
    label: 'Seller official name',
    severity: 'recommended',
  },
  {
    key: 'seller_internal_company_code',
    label: 'Seller internal company code',
    severity: 'recommended',
  },
  { key: 'order_date', label: 'Order date', severity: 'recommended' },
  {
    key: 'agreed_delivery_date',
    label: 'Agreed delivery date',
    severity: 'recommended',
  },
  { key: 'custom_tags', label: 'Custom tags', severity: 'recommended' },
  {
    key: 'supplier_active_user',
    label: 'Supplier has an active user',
    severity: 'recommended',
  },
];

/** Keys mirror the UI's `detail.readiness.checks.*` translation keys. */
export const READINESS_CHECK_LABELS: Record<string, string> =
  Object.fromEntries(
    TRACING_READINESS_SEED.map(entry => [entry.key, entry.label]),
  );

/**
 * The list of CMM readiness checks, and the only source of it. Array order is
 * display order and blame order, as in `TRACING_READINESS_SEED`.
 *
 * Nothing seeds this into `automation_workflow_template_requirements` yet, so
 * the APPEND-ONLY rule above does not bind it — the keys are still free to
 * change. The moment a migration freezes them onto the Start CMM template they
 * inherit that rule in full, for exactly the reasons given above. Until then the
 * only consumers are in code: the evaluators, the readiness drawer and the order
 * list column.
 *
 * Kept as its own list rather than a subset of the tracing one. The two answer
 * different questions about the same order, and the day a tracing-only column is
 * added the alternative would quietly make it a CMM concern.
 *
 * `supplier_linked` and `product_reference` are spelled the same as their
 * tracing entries because they verify the same column with the same rule. That
 * is a convenience for a reader, not something the gate can rely on — see
 * `CMM_CHECK_EVALUATORS`.
 *
 * Every entry is `required`. CMM has no advisory half — a record it cannot key
 * or weigh is not a record it can hold, so there is nothing to merely recommend.
 */
export const CMM_READINESS_SEED: ReadinessSeedEntry[] = [
  { key: 'supplier_linked', label: 'Supplier linked', severity: 'required' },
  {
    key: 'supplier_identifier',
    label: 'Supplier identifier (internal company code or network ID)',
    severity: 'required',
  },
  {
    key: 'product_reference',
    label: 'Product / article reference on every line',
    severity: 'required',
  },
  {
    key: 'material_composition',
    label: 'Material composition on every line',
    severity: 'required',
  },
  {
    key: 'certification_standard',
    label: 'Certification standard on every line',
    severity: 'required',
  },
  {
    key: 'quantity_unit',
    label: 'Quantity & unit of measure on every line',
    severity: 'required',
  },
  // No `received_quantity`: it comes from the TC, which reaches CMM only after
  // the send, so no order can have one beforehand. CMM's quantity coverage
  // check covers it once the TC is extracted.
];

/**
 * Keys mirror the UI's `detail.readiness.checks.*` translation keys.
 *
 * Separate from `READINESS_CHECK_LABELS` rather than merged into it: the two
 * seeds share two keys with different wording, and a merge would silently give
 * one workflow the other's label depending on insertion order.
 */
export const CMM_READINESS_CHECK_LABELS: Record<string, string> =
  Object.fromEntries(CMM_READINESS_SEED.map(entry => [entry.key, entry.label]));
